interface WorkspaceFocusReturn {
  focusTarget: HTMLElement | null;
  visibilityTarget?: HTMLElement | null;
}

export type DocumentWorkspacePane = "inventory" | "detail";

export const DOCUMENT_WORKSPACE_PANE_EVENT = "takeoff:show-workspace-pane";

export function showDocumentWorkspacePane(
  pane: DocumentWorkspacePane,
  focusTargetId: string
): void {
  globalThis.window?.dispatchEvent(
    new CustomEvent<{ pane: DocumentWorkspacePane }>(DOCUMENT_WORKSPACE_PANE_EVENT, {
      detail: { pane }
    })
  );

  const focusTarget = () =>
    globalThis.document.getElementById(focusTargetId)?.focus({ preventScroll: false });
  const frame = globalThis.requestAnimationFrame?.(focusTarget);

  if (frame === undefined) {
    focusTarget();
  }
}

/**
 * Restores focus without allowing the browser to align content beneath the
 * sticky document header, then corrects the detail viewport using the header's
 * actual rendered height.
 */
export function restoreWorkspaceFocus({
  focusTarget,
  visibilityTarget
}: WorkspaceFocusReturn): void {
  const restore = () => {
    if (!focusTarget?.isConnected) {
      return;
    }

    focusTarget.focus({ preventScroll: true });
    const target =
      visibilityTarget?.isConnected === true ? visibilityTarget : focusTarget;
    const viewport = target.closest<HTMLElement>(
      'section[aria-label="Selected document"]'
    );
    const stickyHeader = viewport?.querySelector<HTMLElement>(
      "[data-document-sticky-header]"
    );

    if (!viewport || !stickyHeader) {
      return;
    }

    const targetRect = target.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    const stickyRect = stickyHeader.getBoundingClientRect();
    const safeTop = Math.max(viewportRect.top, stickyRect.bottom) + 8;
    const safeBottom = viewportRect.bottom - 8;

    if (targetRect.top < safeTop) {
      viewport.scrollTop -= safeTop - targetRect.top;
    } else if (targetRect.bottom > safeBottom) {
      viewport.scrollTop += targetRect.bottom - safeBottom;
    }
  };
  const frame = globalThis.requestAnimationFrame?.(restore);

  if (frame === undefined) {
    restore();
  }
}
